import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Leaf } from "lucide-react";
import { Ladybug } from "@/components/site/Ladybug";

const PERKS = ["Holistic health consultations", "Ambassador discounts", "Healing classes and teachings", "Ongoing support on the path"];

const Clinic = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 grid lg:grid-cols-12 gap-14 items-start" data-testid="clinic-section">
    <div className="lg:col-span-7">
      <span className="eyebrow rise">Healing Starts Here</span>
      <h1 data-testid="clinic-title" className="font-serif-h font-light text-4xl sm:text-5xl lg:text-6xl text-[#577731] leading-[1.1] mt-4 rise d1">The Healers Clinic</h1>
      <p className="font-serif-h italic text-2xl text-[#b6689a] mt-6 rise d2">For the ones ready to stop surviving and start healing.</p>
      <p className="mt-8 text-lg leading-relaxed text-[#232b1b]/80">
        You have carried it long enough: the fatigue, the pain, the imbalance no one could name. The Healers Clinic is for those ready to take their place at the center of their own healing story.
      </p>
      <p className="mt-5 text-lg leading-relaxed text-[#232b1b]/80">
        Here, you are not a chart or a number. You are a whole person, body, spirit, and lineage.
      </p>
      <h3 className="font-serif-h text-2xl text-[#232b1b] mt-12">Clinic members receive: <Ladybug size={20} rotate={80} /></h3>
      <ul className="mt-6 grid sm:grid-cols-2 gap-4">
        {PERKS.map((p) => (
          <li key={p} className="flex items-center gap-3 rounded-2xl bg-[#b1cc90]/25 px-5 py-4 text-sm">
            <Leaf size={16} className="text-[#577731] shrink-0" /> {p}
          </li>
        ))}
      </ul>
      <Link to="/apply?program=clinic" data-testid="clinic-apply-btn" className="btn btn-olive mt-12">Begin Your Healing: Apply Now</Link>
    </div>
    <div className="lg:col-span-5 relative">
      <Ladybug spot="bl" size={26} rotate={-30} />
      <img src="/img/clinic.jpg" alt="The Healers Clinic apothecary" className="rounded-3xl w-full shadow-[0_40px_80px_-40px_rgba(35,43,27,.5)]" />
    </div>
  </section>
);

const Womb = () => (
  <section id="womb" className="bg-[#dacac1]/45 scroll-mt-20" data-testid="womb-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-5 order-2 lg:order-1 grid grid-cols-2 gap-4">
        <img src="/img/womb-glow.jpg" alt="An expectant mother in a white dress" className="arch aspect-[3/5] object-cover w-full" />
        <img src="/img/womb.jpg" alt="A mother holding her child close" className="rounded-3xl aspect-[3/4] object-cover w-full mt-20" />
      </div>
      <div className="lg:col-span-7 order-1 lg:order-2">
        <span className="eyebrow">A Holistic Mamas Clinic</span>
        <h2 className="font-serif-h font-light text-4xl sm:text-5xl lg:text-6xl text-[#577731] mt-4">The Healing Womb <Ladybug size={24} rotate={200} className="align-top" /></h2>
        <p className="font-serif-h italic text-2xl text-[#b6689a] mt-6">The womb is the first temple. Every mother carries both creation and the weight of it.</p>
        <p className="mt-8 text-lg leading-relaxed text-[#232b1b]/80">
          The Healing Womb is a sanctuary for women and mothers, whether you are preparing to receive life, newly postpartum, or still carrying births from years ago that were never fully tended. We hold you with holistic care, ancestral wisdom, and the gentleness you have given everyone but yourself.
        </p>
        <p className="font-serif-h text-3xl text-[#232b1b] mt-10">Mama, it is your turn to be mothered.</p>
        <Link to="/apply?program=womb" data-testid="womb-apply-btn" className="btn btn-mauve mt-10">Enter the Healing Womb: Apply Now</Link>
      </div>
    </div>
  </section>
);

export default function ClinicWomb() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 100);
  }, [hash]);
  return (
    <div data-testid="clinic-page">
      <Clinic />
      <Womb />
    </div>
  );
}
